import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpenIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  HomeIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import logo from "../assets/logo.jpg";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show navbar when at top of page
      if (currentScrollY < 10) {
        setIsVisible(true);
      }
      // Show navbar when scrolling up, hide when scrolling down
      else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [lastScrollY]);

  const navigation = [
    { name: "Home", href: "/", icon: HomeIcon },
    // { name: "Courses", href: "/courses", icon: AcademicCapIcon },
    { name: "Syllabus", href: "/syllabus", icon: DocumentTextIcon },
    { name: "Reading Material", href: "/reading-material", icon: BookOpenIcon },
    { name: "MCQs", href: "/mcqs", icon: QuestionMarkCircleIcon },
  ];

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  return (
    <nav
      className={`bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50 shadow-elegant transition-transform duration-300 ease-in-out ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="container-classic">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src={logo}
                alt="WebDoko Logo"
                className="w-10 h-10 transition-transform duration-200 group-hover:scale-105"
              />
              <span className="text-2xl font-serif font-semibold text-gray-900 tracking-tight">
                WebDoko
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="flex items-center space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center space-x-2 border-b-2 ${
                      isActiveLink(item.href)
                        ? "text-gray-900 border-gray-900"
                        : "text-gray-600 hover:text-gray-900 border-transparent hover:border-gray-300"
                    }`}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    <span className="uppercase tracking-wide text-xs font-medium">
                      {item.name}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/about"
              className="btn-outline text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200 uppercase tracking-wide"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="btn-primary text-sm uppercase tracking-wide"
            >
              Contact
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500 transition-colors duration-200"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-6 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-4 py-3 text-base font-medium flex items-center space-x-3 transition-colors duration-200 ${
                      isActiveLink(item.href)
                        ? "text-gray-900 bg-gray-50 border-l-4 border-gray-900"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-l-4 border-transparent"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {Icon && <Icon className="w-5 h-5" />}
                    <span className="uppercase tracking-wide text-sm font-medium">
                      {item.name}
                    </span>
                  </Link>
                );
              })}

              {/* Mobile CTA Section */}
              <div className="pt-6 mt-6 border-t border-gray-200 space-y-3">
                <Link
                  to="/about"
                  className="block px-4 py-3 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors duration-200 uppercase tracking-wide text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
                <div className="px-4">
                  <Link
                    to="/contact"
                    className="block w-full btn-primary text-center uppercase tracking-wide text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Contact
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
